/**
 * What the card may say when there is no Dutch value.
 *
 * Until now it said "Analyse in behandeling" for every such car. That was never true: the
 * pipeline writes a definitive `prediction_status` on every served row and nothing is ever
 * left mid-analysis. Measured on the served table 2026-09-13, 277,271 rows (12%) carried a
 * finished refusal and displayed as pending, and NOT ONE row was actually pending. The worst
 * case is the one Coen hit: every car built before 2000 is refused by design (78,557 rows,
 * 0% valued), so filtering the list to the nineties produced a wall of cards that claimed to
 * be working on it and never would.
 *
 * A refusal is a real answer and usually a useful one -- "we cannot value a 1991 Golf from
 * comparables" is what an honest importer says too. So each status gets its own sentence, and
 * an unknown status is unavailable, not evidence that an analysis job is running.
 */

/** kind: 'value' | 'home' | 'refused' | 'unavailable'. `why` explains the label. */
const REFUSALS = {
  collector_market: {
    short: 'No Dutch value',
    why: 'Over 25 years old: condition, history and originality need specialist evidence. Our listing data cannot establish a reliable Dutch value.',
  },
  no_comps: {
    short: 'No comparable Dutch cars',
    why: 'No Dutch listing of this model, fuel and age to price against.',
  },
  no_same_fuel_comps: {
    short: 'No Dutch cars with this fuel',
    why: 'Dutch listings of this model exist, but none with this fuel type. Pricing a diesel off petrol cars is not a valuation.',
  },
  refused_era_mismatch: {
    short: 'Dutch market is a different generation',
    why: 'The Dutch cars of this model year are a different generation of the car. Same name, different vehicle.',
  },
  refused_source_spec_mismatch: {
    short: 'Advert contradicts the specification',
    why: 'The dealer page does not match the specification we scraped, so any value would rest on the wrong car.',
  },
  refused_source_conditional_price: {
    short: 'Price is conditional',
    why: 'The advertised price depends on finance, trade-in or a business buyer, so it is not a purchase price.',
  },
}

/**
 * @param {{pred_status?: string, estimated_sales_price_nl?: number}} car
 * @returns {{kind: string, short: string|null, why: string|null}}
 */
export function valuationStatus(car) {
  const unavailable = { kind: 'unavailable', short: 'Valuation unavailable',
    why: 'The current record does not provide a usable valuation or a recognised reason. This does not mean an analysis is running.' }
  if (!car) return unavailable
  if (Number(car.estimated_sales_price_nl) > 0) return { kind: 'value', short: null, why: null }
  const status = String(car.pred_status || '')
  if (status === 'target_market') {
    return {
      kind: 'home',
      short: 'Dutch car',
      // Not a gap in the model: NL rows ARE the comparison pool, so pricing one against
      // itself would be circular. analyzer.py drops them before the analyzed artifact.
      why: 'This car is already in the Netherlands. Dutch listings are the yardstick we price imports against, so this one has no import value.',
    }
  }
  const refusal = REFUSALS[status]
  if (refusal) return { kind: 'refused', ...refusal }
  return unavailable
}

/** True when the pricing chips (tier, comparables used) have anything to report. */
export function hasPricingEvidence(car) {
  return Number(car?.match_count) > 0 || Number(car?.n) > 0
    || (!!car?.match_tier && car.match_tier !== 'NA')
}

/**
 * Which margin the card leads with.
 *
 * MARGIN-HEADLINE-2. The card led with the GROSS margin while every gate, every refusal
 * sentence use the model-adjusted one. It is not take-home profit. On a served row -- a
 * 2013 Giulietta, listing 225070155 -- that put "+34.9%" in the headline and "margin 17.9%
 * is below the 28.8% required" in the box beneath it. Coen reported the same contradiction
 * twice, on two different cars, which is what a number nobody can reconcile does.
 *
 * The net figure exists only on the VAT-inclusive basis (the haircut and the condition
 * reserve are subtracted from the inclusive revenue), so flipping the card to ex-VAT falls
 * back to that basis's own gross margin rather than mixing the two.
 */
export function headlineMargin(car, { vatIncluded = true, economicsAvailable = true } = {}) {
  const number = value => typeof value === 'number' && Number.isFinite(value) ? value : null
  const net = number(car?.expected_margin_net_pct)
  const gross = vatIncluded ? number(car?.margin_pct)
    : number(car?.expected_margin_pct_excl_vat) == null ? null : car.expected_margin_pct_excl_vat * 100
  const unavailable = { pct: null, eur: null, secondaryPct: null, isNet: false }
  if (!economicsAvailable) return unavailable
  if (net == null || !vatIncluded) return { pct: gross,
    eur: gross == null ? null : number(vatIncluded ? car?.estimated_margin : car?.expected_revenue_excl_vat),
    secondaryPct: null, isNet: false }
  const point = number(car?.estimated_sales_price_nl)
  // A sub-line repeating the same number to one decimal is noise, not transparency.
  return { pct: net * 100, eur: point > 0 ? net * point : null,
    secondaryPct: gross != null && Math.abs(net * 100 - gross) > 1 ? gross : null, isNet: true }
}
